/**
 * Stale-client detection + always-reload (stale-client-detection design, 2026-08-23). A page
 * loaded before a deploy holds an old in-memory module graph and an old boot-time i18n bundle:
 * new keys render as literal strings, and since content hashing a lazy `import()` of a retired
 * hashed chunk 404s and the surface silently degrades. When staleness is CONFIRMED, this module
 * reloads — always, immediately, silently. No prompt, no idle-vs-mid-game split (Andre's call):
 * a reload costs nothing durable, restore is server-authoritative and live-first mid-trick.
 *
 * The oracle is the page's own entry URL. Prod serves it hashed (`/assets/js/skat-<HASH>.js`),
 * and esbuild's hash cascades upward through every statically analyzable import, so the entry's
 * name is a merkle root over all prod JS: it still answers 200 ⇒ every chunk this client holds is
 * still served; it answers 404 ⇒ the JS build output changed ⇒ reload. Probing any non-entry URL
 * (this module's own `import.meta.url`, say) would report a false "fresh" — hashes cascade to
 * importers, not sideways. Dev is structurally inert: the entry is un-hashed `/js/skat.js`,
 * always 200, so no built/dev branching lives anywhere in here.
 *
 * Installed from `boot.js#init` (eager, load-bearing — a recovery module behind a lazy import
 * would itself 404 on exactly the stale client it exists to rescue). Triggers: the two global
 * error listeners `install` wires, the visibility/bfcache probes, and `checkStaleClient()` called
 * directly from the three seams that deliberately swallow a dynamic-import failure.
 */

// why: a burst of rejections from one broken interaction is one question, not many.
const REACTIVE_THROTTLE_MS = 10_000;
// why: an unbounded HEAD can hang for minutes on a flaky mobile network (the TWA case) and the
// in-flight flag it holds disables every trigger for that whole time. The bound must stay under
// REACTIVE_THROTTLE_MS — a probe outliving the throttle window would block reactive triggers longer
// than the throttle intends, moving the wedge rather than closing it; half of it leaves the flag
// clear well before the next reactive trigger is even eligible. An aborted fetch rejects, so it
// lands in the swallow below and the next trigger simply asks again.
const PROBE_TIMEOUT_MS = 5_000;
// play-test: the HEAD probe is cheap, but there's no reason to re-ask on an actively toggled tab
// more often than this — it exists for the tab resumed after hours, not the one flipped twice.
const VISIBILITY_THROTTLE_MS = 5 * 60_000;
const RELOADED_FOR_KEY = "skat.staleReloadedFor";

/** The entry URL, resolved lazily on the first probe and memoized: `undefined` until then, then
 *  the URL or `null` when the page carries no src'd module tag. Lazy rather than resolved at
 *  install so no ordering dependency exists between install and the first choke-point call. */
let entryUrl;
/** The last probe's timestamp — one clock, two thresholds (reactive 10 s / visibility 5 min).
 *  Starts at 0 so the first trigger of either kind may probe immediately. */
let lastProbeAtMs = 0;
let probing = false;

/** Wires the passive triggers. Called once, from boot.js#init. */
export function installStaleClientReload() {
  // No failure-signature matching, deliberately: browser import-error messages differ per engine
  // and are fragile. The probe itself is the discriminator — an error on a genuinely stale client
  // earns the reload we want anyway, and one on a fresh client probes once and changes nothing.
  window.addEventListener("unhandledrejection", () => checkStaleClient());
  window.addEventListener("error", () => checkStaleClient());
  document.addEventListener("visibilitychange", () => {
    // The handler's own gate, not a listener option — it also makes a synthetically dispatched
    // event behave like a real transition in tests.
    if (document.visibilityState !== "visible") return;
    probe(VISIBILITY_THROTTLE_MS);
  });
  // The bfcache path: a restored page never re-runs its own boot, so this is its only trigger.
  window.addEventListener("pageshow", (e) => {
    if (e.persisted) probe(VISIBILITY_THROTTLE_MS);
  });
}

/** The throttled reactive check the swallowing choke points call, one line each, so a stale
 *  client can never keep limping in degraded mode. */
export function checkStaleClient() {
  probe(REACTIVE_THROTTLE_MS);
}

function probe(throttleMs) {
  if (probing) return;
  if (entryUrl === undefined)
    entryUrl =
      document.querySelector('script[type="module"][src]')?.src ?? null;
  // No src'd module tag: the fixture DOM of a unit test, or /test-scenarios (which reaches init()
  // through an inline module tag). Nothing to probe — no-op.
  if (entryUrl == null) return;
  const now = Date.now();
  if (now - lastProbeAtMs < throttleMs) return;
  lastProbeAtMs = now;
  probing = true;
  // why: `no-store` is load-bearing — the entry sits under the immutable /assets/ caching
  // contract, so a cached response would answer 200 forever. HEAD avoids re-downloading the body.
  fetch(entryUrl, {
    method: "HEAD",
    cache: "no-store",
    signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
  })
    .then(
      (res) => {
        // 404 is the ONLY staleness answer. 200, 5xx, anything else: not staleness, do nothing —
        // whatever error triggered this stays exactly as loud as it is today.
        if (res.status === 404) reloadUnlessLooping();
      },
      // The one deliberate, narrow swallow (recovery is this module's whole job): left to
      // propagate, every offline probe would land as a fresh unhandledrejection and re-enter its
      // own trigger. A rejection handler as .then's SECOND argument, not a trailing .catch — the
      // ensureGameUi/ensureResultUi idiom: a trailing catch would also swallow a throw out of the
      // reload path above, which must stay loud (CODING § let errors propagate).
      () => {},
    )
    .finally(() => {
      probing = false;
    });
}

function reloadUnlessLooping() {
  // why: the guard keys on the entry URL already reloaded for, not on elapsed time — at most one
  // automatic reload ATTEMPT per entry URL per tab, ever.
  // On the normal path that costs nothing: a real deploy changes the entry's content hash, so the
  // reload lands this tab on a NEW entry URL, which no record names — the tab stays protected for
  // every future deploy, including a second one landing seconds after the first (a time window
  // would have wrongly suppressed that one).
  // The degenerate path is the accepted price. If the 404 had a non-deploy cause (an edge or
  // object-store glitch), the reload fetches genuinely fresh HTML — prod HTML carries no
  // Cache-Control — that still names this same build, so we see the SAME URL 404 again and stand
  // down. This tab's entry URL can from then on only change through a reload it will never again
  // perform, so a LATER genuine deploy is suppressed for this tab too: it stays exactly as degraded
  // as a pre-feature client would be. Permanent, and deliberately so — the alternative is a tab
  // reloading itself indefinitely against a 404 no reload can fix.
  // sessionStorage is per-tab and survives a reload — exactly the scope this guard needs.
  if (sessionStorage.getItem(RELOADED_FOR_KEY) === entryUrl) {
    console.error(
      "[stale-client] entry URL still 404s after a reload — standing down instead of looping",
    );
    return;
  }
  sessionStorage.setItem(RELOADED_FOR_KEY, entryUrl);
  location.reload();
}
